import React, { useState } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { url } from '../../util/config';

const PasswordField = ({ id, label, value, onChange, onBlur, show, onToggle, error }) => (
    <div style={styles.fieldGroup}>
        <label htmlFor={id} style={styles.label}>{label}</label>
        <div style={styles.inputWrapper}>
            <input
                id={id}
                type={show ? 'text' : 'password'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                style={{ ...styles.input, ...(error ? styles.inputError : {}) }}
                autoComplete="off"
            />
            <button type="button" style={styles.eyeBtn} onClick={onToggle} tabIndex={-1}>
                <i className={`bi ${show ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
        </div>
        {error && <span style={styles.fieldError}>{error}</span>}
    </div>
);

const ChangePasswordComponent = () => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/;

    const validate = (field, value) => {
        const newErrors = { ...errors };
        if (field === 'currentPassword') {
            if (!value) newErrors.currentPassword = 'Current password is required.';
            else delete newErrors.currentPassword;
        }
        if (field === 'newPassword') {
            if (!value) newErrors.newPassword = 'New password is required.';
            else if (!passwordRegex.test(value)) newErrors.newPassword = 'Must be 6+ characters with uppercase, lowercase, number and special character.';
            else delete newErrors.newPassword;
        }
        if (field === 'confirmPassword') {
            if (!value) newErrors.confirmPassword = 'Please confirm your new password.';
            else if (value !== newPassword) newErrors.confirmPassword = 'Passwords do not match.';
            else delete newErrors.confirmPassword;
        }
        setErrors(newErrors);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        // Validate all fields before submit
        const submitErrors = {};
        if (!currentPassword) submitErrors.currentPassword = 'Current password is required.';
        if (!newPassword) submitErrors.newPassword = 'New password is required.';
        else if (!passwordRegex.test(newPassword)) submitErrors.newPassword = 'Must be 6+ characters with uppercase, lowercase, number and special character.';
        if (!confirmPassword) submitErrors.confirmPassword = 'Please confirm your new password.';
        else if (confirmPassword !== newPassword) submitErrors.confirmPassword = 'Passwords do not match.';

        if (Object.keys(submitErrors).length > 0) {
            setErrors(submitErrors);
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/users/changepassword`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ currentPassword, newPassword }),
            });

            if (response.ok) {
                setSuccessMessage('Password changed successfully!');
                setErrorMessage('');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setErrors({});
                // Auto-clear success message after 4 seconds
                setTimeout(() => setSuccessMessage(''), 4000);
            } else {
                const errorData = await response.json();
                setErrorMessage(errorData.error || 'Failed to change password. Please try again.');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            setErrorMessage('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.card}>
                    {/* Card header */}
                    <div style={styles.cardHeader}>
                        <div style={styles.iconCircle}>
                            <i className="bi bi-key" style={{ fontSize: '20px', color: '#1565c0' }}></i>
                        </div>
                        <div>
                            <h5 style={styles.cardTitle}>Change Password</h5>
                            <p style={styles.cardSubtitle}>Update your account password</p>
                        </div>
                    </div>

                    <div style={styles.cardBody}>
                        <form onSubmit={handleSubmit} noValidate>
                            <PasswordField
                                id="currentPassword"
                                label="Current Password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                onBlur={() => validate('currentPassword', currentPassword)}
                                show={showCurrent}
                                onToggle={() => setShowCurrent(!showCurrent)}
                                error={errors.currentPassword}
                            />
                            <PasswordField
                                id="newPassword"
                                label="New Password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                onBlur={() => validate('newPassword', newPassword)}
                                show={showNew}
                                onToggle={() => setShowNew(!showNew)}
                                error={errors.newPassword}
                            />
                            <PasswordField
                                id="confirmPassword"
                                label="Confirm New Password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onBlur={() => validate('confirmPassword', confirmPassword)}
                                show={showConfirm}
                                onToggle={() => setShowConfirm(!showConfirm)}
                                error={errors.confirmPassword}
                            />

                            {/* Password hint */}
                            <p style={styles.hint}>
                                <i className="bi bi-info-circle" style={{ marginRight: 5 }}></i>
                                Password must be 6+ characters and include uppercase, lowercase, a number and a special character (@$!%*?&).
                            </p>

                            {errorMessage && (
                                <div style={styles.errorBanner}>
                                    <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>
                                    {errorMessage}
                                </div>
                            )}
                            {successMessage && (
                                <div style={styles.successBanner}>
                                    <i className="bi bi-check-circle" style={{ marginRight: 8 }}></i>
                                    {successMessage}
                                </div>
                            )}

                            <button type="submit" style={styles.submitBtn} disabled={isLoading}>
                                {isLoading
                                    ? <><span style={styles.spinner}></span> Updating…</>
                                    : 'Update Password'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', justifyContent: 'center', paddingTop: '16px' },
    card: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', width: '100%', maxWidth: '480px', overflow: 'hidden', border: '1px solid #e5e7eb' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    iconCircle: { width: '44px', height: '44px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' },
    cardSubtitle: { margin: 0, fontSize: '13px', color: '#6b7280' },
    cardBody: { padding: '24px' },
    fieldGroup: { marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
    input: { width: '100%', padding: '10px 40px 10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    eyeBtn: { position: 'absolute', right: '10px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '15px', padding: 0, display: 'flex', alignItems: 'center' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    hint: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px', lineHeight: '1.5' },
    errorBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#dc2626', marginBottom: '16px', display: 'flex', alignItems: 'center' },
    successBanner: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#16a34a', marginBottom: '16px', display: 'flex', alignItems: 'center' },
    submitBtn: { width: '100%', padding: '11px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default ChangePasswordComponent;
